import { getErrorMessage } from '../utils/errorMessage';
import { useState, useEffect, useCallback } from 'react';
import {
  Settings, Users, BookOpen, BarChart2, Search, ChevronLeft,
  ChevronRight, Loader2, Check, X, Plus, Edit2, Trash2, Save, Cpu, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import api from '../utils/api';

const TABS = [
  { key: 'stats',       label: '数据看板', icon: BarChart2 },
  { key: 'users',       label: '用户管理', icon: Users },
  { key: 'methodology', label: '方法论库', icon: BookOpen },
  { key: 'models',      label: '模型评测', icon: Cpu },
];

// ── 看板 ──────────────────────────────────────────────────
function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-gray-400" /></div>;

  const cards = [
    { label: '注册用户',    value: stats?.total_users,    color: 'violet' },
    { label: '生成剧本',    value: stats?.total_scripts,  color: 'blue' },
    { label: '审核（7天）', value: stats?.reviews_7d,     color: 'emerald' },
    { label: 'Agent 项目', value: stats?.agent_projects,  color: 'amber' },
  ];

  const colorMap = {
    violet: 'bg-violet-50 border-violet-200 text-violet-600 dark:bg-violet-500/10 dark:border-violet-500/25 dark:text-violet-400',
    blue:   'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-500/10 dark:border-blue-500/25 dark:text-blue-400',
    emerald:'bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-500/10 dark:border-emerald-500/25 dark:text-emerald-400',
    amber:  'bg-amber-50 border-amber-200 text-amber-600 dark:bg-amber-500/10 dark:border-amber-500/25 dark:text-amber-400',
  };

  return (
    <div className="grid grid-cols-2 gap-4 max-w-lg">
      {cards.map(c => (
        <div key={c.label} className={`border rounded-2xl p-5 ${colorMap[c.color]}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
          <p className="text-3xl font-bold">{c.value ?? '—'}</p>
        </div>
      ))}
    </div>
  );
}

// ── 用户管理 ──────────────────────────────────────────────
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, credits, role }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users', { params: { page, q } });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch {}
    finally { setLoading(false); }
  }, [page, q]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${editing.id}`, { credits: parseInt(editing.credits), role: editing.role });
      setUsers(prev => prev.map(u => u.id === editing.id ? { ...u, ...r.data } : u));
      setEditing(null);
    } catch (e) {
      alert(getErrorMessage(e, '保存失败'));
    } finally { setSaving(false); }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="搜索用户名或邮箱"
            className="input pl-8 text-sm py-2 w-full"
          />
        </div>
        <span className="text-xs text-gray-400 self-center">共 {total} 位用户</span>
      </div>

      <div className="bg-white dark:bg-[#14141a] border border-gray-200 dark:border-white/[0.07] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-white/[0.03]">
              <tr>
                {['用户', '邮箱', '积分', '角色', '注册时间', '操作'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{u.username}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    {editing?.id === u.id ? (
                      <input
                        type="number"
                        value={editing.credits}
                        onChange={e => setEditing(prev => ({ ...prev, credits: e.target.value }))}
                        className="input w-20 py-1 text-sm"
                      />
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{u.credits}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editing?.id === u.id ? (
                      <select
                        value={editing.role}
                        onChange={e => setEditing(prev => ({ ...prev, role: e.target.value }))}
                        className="input py-1 text-sm"
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    ) : (
                      <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${u.role === 'admin' ? 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400' : 'bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400'}`}>
                        {u.role}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {editing?.id === u.id ? (
                      <div className="flex gap-1">
                        <button onClick={saveEdit} disabled={saving} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 rounded-lg transition-colors">
                          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                        </button>
                        <button onClick={() => setEditing(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-colors">
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setEditing({ id: u.id, credits: u.credits, role: u.role })}
                        className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-lg transition-colors">
                        <Edit2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors">
            <ChevronLeft size={15} />
          </button>
          <span className="text-xs text-gray-400">第 {page} / {totalPages} 页</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors">
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── 方法论库 ──────────────────────────────────────────────
function MethodologyPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'ruler', name: '', description: '', content: '', tags: '', genre: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/methodology', { params: typeFilter ? { type: typeFilter } : {} });
      setItems(r.data);
    } catch {} finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const saveItem = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(/[,，\s]+/).filter(Boolean) };
      if (editing) {
        const r = await api.put(`/admin/methodology/${editing}`, payload);
        setItems(prev => prev.map(i => i.id === editing ? r.data : i));
      } else {
        const r = await api.post('/admin/methodology', payload);
        setItems(prev => [r.data, ...prev]);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ type: 'ruler', name: '', description: '', content: '', tags: '', genre: '' });
    } catch (e) {
      alert(getErrorMessage(e, '保存失败'));
    } finally { setSaving(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('确定删除这条规则？')) return;
    await api.delete(`/admin/methodology/${id}`).catch(() => {});
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ type: item.type, name: item.name, description: item.description || '', content: item.content, tags: (item.tags || []).join(', '), genre: item.genre || '' });
    setShowForm(true);
  };

  const statusColor = { active: 'text-emerald-600', candidate: 'text-amber-500', deprecated: 'text-red-400' };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {[['','全部'], ['ruler','Rulers'], ['skill','Skills']].map(([val, label]) => (
            <button key={val} onClick={() => setTypeFilter(val)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${typeFilter === val ? 'bg-violet-600 text-white' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06]'}`}>
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => { setEditing(null); setForm({ type: 'ruler', name: '', description: '', content: '', tags: '', genre: '' }); setShowForm(true); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold rounded-xl transition-colors"
        >
          <Plus size={12} />新增
        </button>
      </div>

      {/* 表单 */}
      {showForm && (
        <div className="bg-white dark:bg-[#14141a] border border-violet-200 dark:border-violet-500/30 rounded-2xl p-4 mb-4 space-y-3">
          <div className="flex gap-2">
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="input text-sm py-2 w-28">
              <option value="ruler">ruler</option>
              <option value="skill">skill</option>
            </select>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="规则名称" className="flex-1 input text-sm py-2" />
            <input value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))} placeholder="题材（空=通用）" className="w-32 input text-sm py-2" />
          </div>
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="简介" className="w-full input text-sm py-2" />
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="规则/技能完整内容（注入到 AI prompt 的文本）" rows={6} className="w-full input text-sm py-2 resize-none" />
          <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="标签（逗号分隔，如：对话,节奏,通用）" className="w-full input text-sm py-2" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-xl transition-colors">取消</button>
            <button onClick={saveItem} disabled={saving || !form.name || !form.content}
              className="flex items-center gap-1 px-4 py-1.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-xs font-semibold rounded-xl transition-colors">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {editing ? '保存修改' : '添加'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="bg-white dark:bg-[#14141a] border border-gray-200 dark:border-white/[0.07] rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md ${item.type === 'ruler' ? 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400'}`}>
                      {item.type}
                    </span>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item.name}</span>
                    {item.genre && <span className="text-[10px] text-gray-400">#{item.genre}</span>}
                    <span className={`text-[10px] font-medium ${statusColor[item.status] || 'text-gray-400'}`}>● {item.status}</span>
                    <span className="text-[10px] text-gray-400">使用 {item.usage_count} 次</span>
                  </div>
                  {item.description && <p className="text-xs text-gray-400 mb-1.5">{item.description}</p>}
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3 font-mono bg-gray-50 dark:bg-white/[0.03] rounded-lg px-2 py-1.5">
                    {item.content}
                  </p>
                  {item.tags?.length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {item.tags.map(t => <span key={t} className="text-[10px] bg-gray-100 dark:bg-white/[0.06] text-gray-400 px-1.5 py-0.5 rounded-md">{t}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => startEdit(item)} className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-lg transition-colors">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => deleteItem(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-sm text-gray-400 py-10">暂无内容</p>}
        </div>
      )}
    </div>
  );
}

// ── 模型评测 ──────────────────────────────────────────────
const AGENT_LABELS = { bible:'创意设定', outline:'分集大纲', script:'剧本编写', consistency:'一致性检查', chat:'通用对话' };
const MODEL_COLOR = {
  'gemini-2.5-pro':       'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20',
  'gemini-3.1-pro-preview':'bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20',
  'deepseek-v4-flash':    'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20',
  'deepseek-v4-pro':      'bg-teal-50 text-teal-600 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20',
};

function ModelEvalPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/model-stats')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-gray-400" /></div>;

  const ratingBar = (good, bad, rated) => {
    if (!rated || rated === '0') return <span className="text-gray-300 text-xs">暂无评分</span>;
    const goodPct = Math.round((good / rated) * 100);
    const badPct  = Math.round((bad  / rated) * 100);
    return (
      <div className="flex items-center gap-1.5">
        <ThumbsUp size={11} className="text-emerald-500 shrink-0" />
        <span className="text-xs text-emerald-600 font-semibold">{goodPct}%</span>
        <div className="flex-1 h-1.5 bg-gray-100 dark:bg-white/[0.06] rounded-full overflow-hidden min-w-[60px]">
          <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${goodPct}%` }} />
        </div>
        <ThumbsDown size={11} className="text-red-400 shrink-0" />
        <span className="text-xs text-red-500 font-semibold">{badPct}%</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 按模型汇总 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">模型总览</h3>
        <div className="grid grid-cols-1 gap-3">
          {(data?.byModel || []).length === 0 && (
            <p className="text-sm text-gray-400 py-6 text-center">暂无数据，需先在 agent_chat_history 表加 model_used 字段并积累调用记录</p>
          )}
          {(data?.byModel || []).map(row => (
            <div key={row.model_used} className={`border rounded-2xl p-4 ${MODEL_COLOR[row.model_used] || 'bg-gray-50 border-gray-200 dark:bg-white/[0.03] dark:border-white/[0.07]'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold">{row.model_used}</span>
                <span className="text-xs opacity-60">{row.calls} 次调用 · {row.rated} 条评分</span>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-xs opacity-60">平均评分</span>
                  <p className="text-xl font-bold">{row.avg_rating ?? '—'}</p>
                </div>
                <div className="flex-1">{ratingBar(row.good, row.bad, row.rated)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 按 Agent 类型分布 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">各 Agent 明细</h3>
        <div className="bg-white dark:bg-[#14141a] border border-gray-200 dark:border-white/[0.07] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-white/[0.03]">
              <tr>
                {['Agent', '使用模型', '调用次数', '平均评分'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              {(data?.byAgent || []).map((row, i) => (
                <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">
                    {AGENT_LABELS[row.agent_type] || row.agent_type}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${MODEL_COLOR[row.model_used] || 'bg-gray-100 text-gray-500'}`}>
                      {row.model_used || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{row.calls}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{row.avg_rating ?? '—'}</td>
                </tr>
              ))}
              {(data?.byAgent || []).length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">暂无数据</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── 主页面 ────────────────────────────────────────────────
export default function Admin() {
  const [tab, setTab] = useState('stats');

  return (
    <div>
      <div className="max-w-4xl mx-auto">

        <div className="flex items-center gap-3 mb-7">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 dark:from-gray-400 dark:to-gray-600 flex items-center justify-center">
            <Settings size={17} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">后台管理</h1>
            <p className="text-xs text-gray-400 mt-0.5">仅限管理员访问</p>
          </div>
        </div>

        {/* Tab 切换 */}
        <div className="flex gap-1 bg-gray-100 dark:bg-white/[0.04] p-1 rounded-xl mb-6 w-fit">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === t.key
                    ? 'bg-white dark:bg-[#1a1a22] text-gray-800 dark:text-gray-200 shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                }`}>
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'stats'       && <StatsPanel />}
        {tab === 'users'       && <UsersPanel />}
        {tab === 'methodology' && <MethodologyPanel />}
        {tab === 'models'      && <ModelEvalPanel />}
      </div>
    </div>
  );
}
